import React, { ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered in place of `children` once a render error has been caught. */
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  isError: boolean;
}

/**
 * Generic error boundary. Catches render errors in its subtree, reports them to
 * Sentry once (with a populated `componentStack`), and renders `fallback`.
 *
 * The patient app mounts this twice (see `index.tsx` and `App.tsx`): an outer
 * boundary outside the providers with a context-free fallback for fatal errors,
 * and an inner boundary inside the providers whose fallback is the real
 * `ErrorPage` (with a working "Recommencer").
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { isError: false };
  }

  // Pure: React passes only `error` here, never `errorInfo`. Reading
  // `errorInfo` (or calling Sentry, which destructures `componentStack` off it)
  // would throw inside the boundary's own error path and unmount the whole tree.
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { isError: true };
  }

  // The only lifecycle React passes `errorInfo` to, so the only safe place to
  // report to Sentry with a populated `componentStack`.
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureReactException(error, errorInfo);
  }

  render() {
    if (this.state.isError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}
